/* ========================================================================= *
 * bowlFunnel.js — the pure display logic for the Bowl Eligibility funnel.
 *
 * Brian: *"looking at bowl eligible - i now need the KPI's - reach out -
 * artwork - etc"*
 *
 * 🔴 THE ONE RULE THIS FILE ENFORCES ON THE RENDERER: NEVER A SILENT ZERO.
 * A zero in a funnel reads as "nobody did it". The truth in five of these seven
 * stages is "we cannot see it", and Brian will act differently on each. So
 * `cell()` below returns a THIRD state — `unknown` — that the component is
 * obliged to render as a word with its reason attached, and `nz()` refuses to
 * print a number for anything that is not one.
 *
 * ⚠️ AND `false` IS NOT `null`. Notre Dame has no desk: we checked all 221
 * bodies and the answer is genuinely nobody, so that renders as a NAMED GAP in
 * red. Its outreach is unknown: nothing could have been recorded. Rendering
 * those the same way would hide the difference between a finding and a blind
 * spot, which is the whole point of the lane.
 * ========================================================================= */

/**
 * The chain, in the order Brian asked for it.
 *
 * ⚠️ STAGE 2 IS "ASSIGNED TO A REP", NOT "OWNED BY A REP", AND THE RENAME IS
 * NOT COSMETIC. Brian's words for it were "owned by a rep", and
 * check-ownership-language.mjs refused the label on the first build — correctly:
 * a record is ASSIGNED to a rep, not owned by them, and SR's standing rule is
 * that a rep is assigned opportunities rather than given a territory. The stage
 * `id` stays `owned` because that is the API contract; only what a human reads
 * changed.
 */
export const STAGES = [
  { id: 'eligible',          label: 'Eligible',          short: 'Eligible' },
  { id: 'owned',             label: 'Assigned to a rep', short: 'Assigned' },
  { id: 'reached_out',       label: 'Reached out',        short: 'Reached' },
  { id: 'artwork_requested', label: 'Artwork requested',  short: 'Art req' },
  { id: 'artwork_delivered', label: 'Artwork delivered',  short: 'Art del' },
  { id: 'deal_created',      label: 'Deal created',       short: 'Deal' },
  { id: 'closed',            label: 'Closed',             short: 'Closed' },
]

/**
 * A stage id → the label a human reads. Used for the latency rows, which the
 * API gives as ids: "eligible → owned" is a contract, not a sentence.
 * Falls back to the id so an unknown stage shows as itself rather than blank.
 */
export const stageName = (id) => STAGES.find((s) => s.id === id)?.label || id

/** How a stage's standing reads to a human, and how it is coloured. */
export const STANDING_LABEL = {
  measurable: 'measurable now',
  fixable: 'measurable with a named fix',
  not_instrumented: 'not instrumented',
}

export const STANDING_LEVEL = {
  measurable: 'ok',
  fixable: 'warn',
  not_instrumented: 'bad',
}

/**
 * A number, or an em dash — and NEVER a 0 standing in for a null.
 *
 * ⚠️ `0` IS PRINTED WHEN IT IS REAL. The distinction is the type, not the
 * value: a measured zero is a number and prints as 0; an unmeasurable stage is
 * null and prints as a dash whose reason sits next to it. Coercing with `||`
 * here is how "we cannot see it" became "nobody did it" in the first place.
 */
export const nz = (n) => (typeof n === 'number' ? n.toLocaleString() : '—')

/** Days, to one decimal, or null. Never 0 for a missing stamp. */
export const nd = (n) => (typeof n === 'number' ? `${n} day${n === 1 ? '' : 's'}` : null)

/**
 * Classify one cell of one team's chain into exactly three states.
 *
 * Returns `{ kind, text, reason }` where kind is:
 *   'yes'     — reached, and we can show it
 *   'gap'     — a REAL zero. We looked and the answer is nobody.
 *   'unknown' — we cannot see it. The reason is mandatory and is carried along.
 */
export function cell(c) {
  if (!c) return { kind: 'unknown', text: 'unknown', reason: 'No cell was returned for this stage.' }
  if (c.reached === true) return { kind: 'yes', text: 'yes', reason: c.caveat || c.basis || '' }
  if (c.reached === false) {
    return { kind: 'gap', text: 'none', reason: c.why || c.basis || '' }
  }
  /* reached == null. The reason is the whole content of this cell. */
  return {
    kind: 'unknown',
    text: 'cannot see',
    reason: c.reason || 'No reason was given, which is itself a defect — report it.',
  }
}

/**
 * The one sentence a stage card leads with.
 *
 * A measurable stage leads with its DERIVATION, because a figure with no
 * derivation is worse than none. An unmeasurable one leads with its REASON,
 * because the reason is the finding.
 */
export function lead(stage) {
  if (!stage) return ''
  return stage.count === null ? (stage.reason || '') : (stage.basis || '')
}

/**
 * Does this stage carry an amount it is refusing to show? Money is
 * manager-only, so the flag is rendered and the figure never is.
 */
export const implied = (stage) => Boolean(stage?.valueImplied)

/** The stages a reader should look at first: the ones with a fix in reach. */
export function fixable(stages = []) {
  return stages.filter((s) => s.standing === 'fixable' && s.fix)
}

/* ---- the sentences the surface says in its own voice ------------------- */

export const FUNNEL_LEAD =
  'The chain from a team crossing six wins to a closed order, per team. Latency between stages is '
  + 'the number worth reviewing — "became eligible on the 12th, first contact on the 19th" — not a '
  + 'coverage percentage.'

export const NULL_NOTE =
  'A stage that cannot be computed shows "cannot see" with its reason, never a zero. A zero in a '
  + 'funnel reads as "nobody did it"; in five of these stages the truth is that we cannot see it, '
  + 'and those call for different actions.'

export const GAP_NOTE =
  'A stage marked "none" in red is a REAL zero — it was looked for and the answer is genuinely '
  + 'nobody. That is a finding, not a blind spot, and it is not the same as "cannot see".'

export const AGE_NOTE =
  'Days since the trigger is an AGE, not a latency: it is now minus the date the team crossed six '
  + 'wins. It is the clock the first-contact gap will be measured against, and it only goes up.'

export const MONEY_NOTE =
  'Money is manager-only. A stage that implies a dollar figure is flagged as carrying value and no '
  + 'amount is shown here at any role.'

export const UNAVAILABLE_NOTE =
  'The funnel could not be read. No stage below is a measurement — this is a failed read, not a '
  + 'funnel of zeros.'

export const LOADING_NOTE = 'Working out which stages can be measured…'
